/**
 * Ícones do painel.
 *
 * Desenhados aqui, em vez de emoji ou biblioteca. Emoji muda de forma em cada
 * sistema operacional, chega colorido no meio de uma paleta contida e é o sinal
 * mais rápido de "isto é um protótipo" — o que trabalha contra o preço num
 * produto vendido como inovação.
 *
 * Todos no mesmo traço e na mesma grade de 24, senão a barra fica desalinhada
 * de um item para o outro.
 *
 * Os 21 primeiros são os originais do painel. Os demais foram acrescentados
 * quando as telas novas precisaram de gestos que não existiam antes (assumir
 * conversa, etiquetar, anotar, alternar tema) — desenhados na mesma grade,
 * pelo mesmo motivo. Uma biblioteca aqui devolveria o traço de todo mundo.
 */

/** O envelope serve tanto para "modelos" quanto para "convidar por e-mail". */
const ENVELOPE = (
  <>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="m3.5 7 7.4 5.2a2 2 0 0 0 2.2 0L20.5 7" />
  </>
);

/** A caixa de arquivo, com a tampa separada — base de arquivar e restaurar. */
const CAIXA = (
  <>
    <rect x="3" y="4" width="18" height="4.6" rx="1.3" />
    <path d="M5 8.6V19a1.6 1.6 0 0 0 1.6 1.6h10.8A1.6 1.6 0 0 0 19 19V8.6" />
  </>
);

export type NomeIcone =
  // ── os originais ──
  | "conversas" | "pacientes" | "agenda" | "funil"
  | "relatorio" | "modelos" | "empresa" | "conexao"
  | "sistema" | "configuracoes" | "sair"
  | "whatsapp" | "copiar" | "check" | "cadeado" | "lupa" | "enviar" | "info" | "alerta" | "reabrir"
  // ── acrescentados para as telas novas ──
  | "seta-direita"
  | "mais" | "fechar" | "lixeira" | "editar" | "salvar"
  | "agente" | "etiqueta" | "nota" | "equipe" | "pessoa"
  | "olho" | "olho-fechado" | "ajustes" | "escudo" | "bancada"
  | "arquivar" | "restaurar" | "entrar" | "desconectar"
  | "sol" | "lua" | "email"
  // ── visual novo (06/10/2026) ──
  | "microfone" | "imagem" | "painel-lateral" | "voltar";

const CAMINHOS: Record<NomeIcone, React.ReactNode> = {
  conversas: <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.3-.6L3 21l1.8-5.1A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4Z" />,
  pacientes: (
    <>
      <path d="M16 20v-1.8a3.6 3.6 0 0 0-3.6-3.6H6.6A3.6 3.6 0 0 0 3 18.2V20" />
      <circle cx="9.5" cy="7.4" r="3.4" />
      <path d="M21 20v-1.8a3.6 3.6 0 0 0-2.7-3.5M15.8 4.2a3.6 3.6 0 0 1 0 6.5" />
    </>
  ),
  agenda: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  funil: <path d="M3.5 4.5h17l-6.4 7.6V19l-4.2 2v-8.9L3.5 4.5Z" />,
  relatorio: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  modelos: ENVELOPE,
  empresa: (
    <>
      <path d="M4 21V6.5a1.5 1.5 0 0 1 1.5-1.5h6A1.5 1.5 0 0 1 13 6.5V21M13 21V11h5.5A1.5 1.5 0 0 1 20 12.5V21M2.5 21h19" />
      <path d="M7.2 9h2.6M7.2 13h2.6M7.2 17h2.6M16 15h1.5" />
    </>
  ),
  conexao: (
    <>
      <path d="M9.5 14.5 5.8 18.2a3.9 3.9 0 0 1-5.5-5.5M14.5 9.5l3.7-3.7a3.9 3.9 0 0 1 5.5 5.5" transform="translate(0.2 0)" />
      <path d="M9 15l6-6" />
    </>
  ),
  sistema: <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h5L11 8h8.5A1.5 1.5 0 0 1 21 9.5v8A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5Z" />,
  configuracoes: (
    <>
      <circle cx="12" cy="12" r="7.3" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M19.5 12h2.5M15.7 18.5 17 20.7M8.3 18.5 7 20.7M4.5 12H2M8.3 5.5 7 3.3M15.7 5.5 17 3.3" />
    </>
  ),
  sair: (
    <>
      <path d="M15.5 16.5 20 12l-4.5-4.5M20 12H9" />
      <path d="M12 20H6.5A2.5 2.5 0 0 1 4 17.5v-11A2.5 2.5 0 0 1 6.5 4H12" />
    </>
  ),
  whatsapp: (
    <path d="M17.5 6.5A7.8 7.8 0 0 0 12 4.2C7.7 4.2 4.2 7.7 4.2 12c0 1.4.4 2.7 1 3.9L4 20l4.2-1.1c1.1.6 2.4.9 3.8.9 4.3 0 7.8-3.5 7.8-7.8 0-2.1-.8-4-2.3-5.5zM12 18.5c-1.2 0-2.3-.3-3.3-.9l-.2-.1-2.5.7.7-2.4-.2-.3c-.7-1-1.1-2.2-1.1-3.5 0-3.6 2.9-6.5 6.5-6.5 1.7 0 3.4.7 4.6 1.9 1.2 1.2 1.9 2.9 1.9 4.6 0 3.6-2.9 6.5-6.4 6.5zm3.5-4.9c-.2-.1-1.1-.6-1.3-.6-.2-.1-.3-.1-.5.1-.1.2-.5.6-.6.8-.1.1-.2.2-.4.1-.2-.1-.9-.3-1.7-1-.6-.6-1-1.3-1.1-1.5-.1-.2 0-.3.1-.4.1-.1.2-.2.3-.4.1-.1.1-.2.2-.3 0-.1 0-.2-.1-.3-.1-.1-.5-1.2-.7-1.6-.2-.4-.4-.3-.5-.4h-.4c-.1 0-.4.1-.6.3-.2.2-.8.8-.8 1.9 0 1.1.8 2.2 1 2.3.1.2 1.6 2.5 4 3.5.6.2 1 .4 1.4.5.6.2 1.1.2 1.6.1.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2-.1-.1-.2-.2-.4-.3z" />
  ),
  copiar: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M4 16V6a2 2 0 0 1 2-2h10" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  cadeado: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M7 10V7a5 5 0 0 1 10 0v3" />
    </>
  ),
  lupa: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  enviar: <path d="m22 2-7 20-4-9-9-4 20-7zM11 13l11-11" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M12 12v4" />
    </>
  ),
  alerta: (
    <>
      <path d="m21.7 18-8.5-15a1.4 1.4 0 0 0-2.4 0L2.3 18A1.4 1.4 0 0 0 3.5 20h17a1.4 1.4 0 0 0 1.2-2z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  reabrir: (
    <>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </>
  ),

  // ── acrescentados ──
  "seta-direita": <path d="M4 12h15M13 6l6 6-6 6" />,
  mais: <path d="M12 5v14M5 12h14" />,
  fechar: <path d="M18 6 6 18M6 6l12 12" />,
  lixeira: (
    <>
      <path d="M4 7h16M9 7V4.6A1.6 1.6 0 0 1 10.6 3h2.8A1.6 1.6 0 0 1 15 4.6V7" />
      <path d="M6.2 7 7 19.4A1.7 1.7 0 0 0 8.7 21h6.6a1.7 1.7 0 0 0 1.7-1.6L17.8 7" />
      <path d="M10.3 11v6M13.7 11v6" />
    </>
  ),
  editar: (
    <>
      <path d="M4 20.2h4L19.4 8.8a2.2 2.2 0 0 0-3.1-3.1L4.9 17.1v3.1Z" />
      <path d="m14.6 7.4 3.1 3.1" />
    </>
  ),
  salvar: (
    <>
      <path d="M5 3.5h11L19.5 7v12.5a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-14a2 2 0 0 1 2-2Z" />
      <path d="M8.5 3.5v5h6v-5M8.5 21.5v-6h7v6" />
    </>
  ),
  agente: (
    <>
      <rect x="4" y="8" width="16" height="12.5" rx="3" />
      <path d="M12 8V4.6M9.6 4.6h4.8M1.8 13v3M22.2 13v3" />
      <path d="M9.2 13v1.6M14.8 13v1.6" />
    </>
  ),
  etiqueta: (
    <>
      <path d="M3.6 11.3V4.9a1.3 1.3 0 0 1 1.3-1.3h6.4a1.3 1.3 0 0 1 .9.4l8 8a1.3 1.3 0 0 1 0 1.9l-6.4 6.4a1.3 1.3 0 0 1-1.9 0l-8-8a1.3 1.3 0 0 1-.3-.9Z" />
      <path d="M7.6 7.6h.01" />
    </>
  ),
  nota: (
    <>
      <path d="M20.5 13.6 13.6 20.5H6a2 2 0 0 1-2-2V5.5a2 2 0 0 1 2-2h12.5a2 2 0 0 1 2 2Z" />
      <path d="M20.5 13.6H15a1.4 1.4 0 0 0-1.4 1.4v5.5" />
    </>
  ),
  equipe: (
    <>
      <circle cx="9.8" cy="8" r="3.8" />
      <path d="M3 20.4a6.8 6.8 0 0 1 13.6 0" />
      <path d="M16.4 4.6a3.8 3.8 0 0 1 0 6.8M18 14.4a6.6 6.6 0 0 1 3 5.4" />
    </>
  ),
  pessoa: (
    <>
      <circle cx="12" cy="8" r="4.2" />
      <path d="M4.6 20.4a7.4 7.4 0 0 1 14.8 0" />
    </>
  ),
  olho: (
    <>
      <path d="M2.4 12S6 5.6 12 5.6 21.6 12 21.6 12 18 18.4 12 18.4 2.4 12 2.4 12Z" />
      <circle cx="12" cy="12" r="3.1" />
    </>
  ),
  "olho-fechado": (
    <>
      <path d="M4 4.2 20 20.2" />
      <path d="M10.2 5.9A9.9 9.9 0 0 1 12 5.6c6 0 9.6 6.4 9.6 6.4a17.6 17.6 0 0 1-3.5 4.2" />
      <path d="M6.6 8.1A17.4 17.4 0 0 0 2.4 12S6 18.4 12 18.4a9.9 9.9 0 0 0 2.8-.4" />
      <path d="M9.9 9.9a3.1 3.1 0 0 0 4.3 4.3" />
    </>
  ),
  ajustes: (
    <>
      <path d="M4 7.5h8.5M17.5 7.5H20M4 16.5h2.5M11.5 16.5H20" />
      <circle cx="15" cy="7.5" r="2.4" />
      <circle cx="9" cy="16.5" r="2.4" />
    </>
  ),
  escudo: (
    <>
      <path d="M12 3.2 19.4 6v5.6c0 4.6-3.1 8.3-7.4 9.4-4.3-1.1-7.4-4.8-7.4-9.4V6Z" />
      <path d="m9 11.9 2.2 2.2 4.1-4.3" />
    </>
  ),
  bancada: (
    <>
      <path d="M9.2 3.2v6.4l-4.9 8.3A1.7 1.7 0 0 0 5.8 20.5h12.4a1.7 1.7 0 0 0 1.5-2.6l-4.9-8.3V3.2" />
      <path d="M8 3.2h8M7.4 14.6h9.2" />
    </>
  ),
  arquivar: (
    <>
      {CAIXA}
      <path d="M10 12.6h4" />
    </>
  ),
  restaurar: (
    <>
      {CAIXA}
      <path d="M12 18.8v-6.4M9.2 15.2 12 12.4l2.8 2.8" />
    </>
  ),
  entrar: (
    <>
      <path d="M15 12H4M11.5 8.5 15 12l-3.5 3.5" />
      <path d="M12 4h5.5A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5H12" />
    </>
  ),
  desconectar: (
    <>
      <path d="M10.4 13.6 7 17a3.5 3.5 0 0 1-5-5l3.4-3.4" />
      <path d="M13.6 10.4 17 7a3.5 3.5 0 0 1 5 5l-3.4 3.4" />
      <path d="M14.5 3.8V2M20.2 9.5H22M3.8 14.5H2M9.5 20.2V22" />
    </>
  ),
  sol: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.2v2.4M12 19.4v2.4M4.5 4.5 6.2 6.2M17.8 17.8l1.7 1.7M2.2 12h2.4M19.4 12h2.4M4.5 19.5l1.7-1.7M17.8 6.2l1.7-1.7" />
    </>
  ),
  lua: <path d="M20.6 14.6A8.7 8.7 0 0 1 9.4 3.4a8.7 8.7 0 1 0 11.2 11.2Z" />,
  email: ENVELOPE,
  microfone: (
    <>
      <rect x="9" y="2.8" width="6" height="11.4" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5v3.7" />
    </>
  ),
  imagem: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="8.8" cy="9.6" r="1.7" />
      <path d="m21 16-4.6-4.6a1.6 1.6 0 0 0-2.3 0L5 20" />
    </>
  ),
  "painel-lateral": (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="M14.5 4.5v15" />
    </>
  ),
  voltar: <path d="M15 5.5 8.5 12l6.5 6.5" />,
};

export function Icone({ nome, className }: { nome: NomeIcone; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className ?? "h-[19px] w-[19px]"}
    >
      {CAMINHOS[nome]}
    </svg>
  );
}

export default Icone;
